import icn_line_chart from "assets/images/icons/icn_line_chart.png";
import icn_info_solid from "assets/images/icons/icn_Info_solid.svg";
import { ReactComponent as Clock } from "assets/images/icons/icn_display_clock.svg";
import { useTheme } from "@mui/material/styles";

import React, { useState } from "react";
import {
  Box,
  Typography,
  Tooltip,
  tooltipClasses,
  CircularProgress,
} from "@mui/material";
import { styled } from "@mui/material/styles";

import WirelessCongestionLevelChart from "../../charts/WirelessCongestionLevelChart";

const HtmlTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: "#4D4D4D",
    color: theme.palette.primary.white,
    maxWidth: 600,
    fontSize: theme.typography.pxToRem(12),
    padding: "16px 20px",
    borderRadius: "8px",
    lineHeight: "1.5",
    boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.3)",
  },
}));

const ActionList = ({ items }) => (
  <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0 }}>
    {items.map((item, index) => (
      <Box component="li" key={index} sx={{ mb: 0.5 }}>
        {item}
      </Box>
    ))}
  </Box>
);

const TooltipContent = () => (
  <Box>
    <Box sx={{ mb: 2.5 }}>
      <Typography sx={{ whiteSpace: "pre-line", fontSize: "12px", mb: 1 }}>
        {`· An AP is congested when its client count exceeds the Client Count Threshold (default: 30, adjustable). Health is the percentage of congested APs, using fixed 10% / 20% bands.`}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: "bold", mb: 0.5 }}>
        Good — Congested APs &lt; 10%
      </Typography>
      <Typography variant="body2" sx={{ fontSize: "12px", mb: 1 }}>
        Client distribution is healthy, ensuring stable connectivity and
        performance.​
      </Typography>
    </Box>

    <Box sx={{ mb: 2.5 }}>
      <Typography variant="body2" sx={{ fontWeight: "bold", mb: 0.5 }}>
        Warning — 10% ≤ Congested APs&lt; 20%
      </Typography>
      <Typography variant="body2" sx={{ fontSize: "12px", mb: 1 }}>
        Some APs exceed the client threshold at peak times. Affected zones may
        see lower throughput or higher latency.​
      </Typography>

      <Typography variant="body2" sx={{ fontWeight: "bold", mb: 0.5 }}>
        Recommended Actions:
      </Typography>
      <Typography variant="body2" sx={{ fontSize: "12px", mb: 1 }}>
        <ActionList
          items={[
            "· Optimize Client Load Balancing toward underutilized neighbor APs.",
            "· Adjust the Client Count Threshold for high-density environments.​",
          ]}
        />
      </Typography>
    </Box>

    <Box sx={{ mb: 2.5 }}>
      <Typography variant="body2" sx={{ fontWeight: "bold", mb: 0.5 }}>
        Critical — Congested APs ≥ 20%
      </Typography>
      <Typography variant="body2" sx={{ fontSize: "12px", mb: 1 }}>
        Widespread AP congestion is causing slow speeds, packet retries, and
        connection drops.
      </Typography>

      <Typography variant="body2" sx={{ fontWeight: "bold", mb: 0.5 }}>
        Recommended Actions:
      </Typography>
      <Typography variant="body2" sx={{ fontSize: "12px", mb: 1 }}>
        <ActionList
          items={[
            "· Deploy additional APs in congested zones.​",
            "· Enable Band Steering and Airtime Fairness.​",
          ]}
        />
      </Typography>
    </Box>
  </Box>
);

const WirelessCongestionLevel = ({
  onClick,
  loading = false,
  noData = false,
  wirelessCongestionChartSeriesData = [],
  analysisData = {},
}) => {
  const theme = useTheme();
  const statusColorMap = {
    Good: theme.palette.primary.greenPrimary,
    Warn: theme.palette.primary.orangeThird,
    Critical: theme.palette.primary.redPrimary,
  };
  const isNoData = noData || Number(analysisData?.totalAps) === 0;

  return (
    <Box
      onClick={isNoData ? undefined : onClick}
      sx={{
        border: `1px solid ${theme.palette.primary.greyThread}`,
        borderRadius: theme.shape.customBorderRadius[8],
        p: 2.5,
        bgcolor: theme.palette.primary.white,
        mb: 3,
        cursor: onClick && !isNoData ? "pointer" : "default",
        "&:hover":
          onClick && !isNoData
            ? {
                boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.05)",
              }
            : {},
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Box
            component="img"
            src={icn_line_chart}
            sx={{ width: "18px", height: "18px", mr: 1 }}
          />

          <Typography
            sx={{
              fontWeight: theme.typography.customFontWeight[2],
              color: theme.palette.primary.main,
              fontSize: theme.typography.customFontSize[3],
              mr: 1,
            }}
          >
            Wireless Congestion Level
          </Typography>

          <HtmlTooltip title={<TooltipContent />} placement="auto">
            <Box
              component="img"
              src={icn_info_solid}
              sx={{ width: "16px", height: "16px", cursor: "pointer" }}
            />
          </HtmlTooltip>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            color: theme.palette.primary.main,
          }}
        >
          <Clock />
          <Typography
            sx={{
              fontSize: theme.typography.customFontSize[2],
              fontWeight: theme.typography.customFontWeight[2],
            }}
          >
            Last 24 Hours
          </Typography>
        </Box>
      </Box>

      {loading ? (
        <Box
          sx={{
            height: "250px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress />
        </Box>
      ) : isNoData ? (
        <Box
          sx={{
            height: "250px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography
            sx={{
              color: theme.palette.primary.greyThird,
              fontSize: theme.typography.customFontSize[2],
            }}
          >
            No data available
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", lg: "row" },
            alignItems: "stretch",
          }}
        >
          <Box
            sx={{
              width: { xs: "100%", lg: 280 },
              pr: { xs: 0, lg: 5 },
              pb: { xs: 3, lg: 0 },
              borderRight: {
                xs: "none",
                lg: `1px solid ${theme.palette.primary.greyThread}`,
              },
            }}
          >
            <Box sx={{ display: "flex", gap: 5, mb: 2.5 }}>
              <Box>
                <Typography
                  sx={{
                    fontSize: theme.typography.customFontSize[1],
                    fontWeight: theme.typography.customFontWeight[3],
                    color: theme.palette.primary.greyPrimary,
                    mb: 0.5,
                  }}
                >
                  Status
                </Typography>

                <Typography
                  sx={{
                    fontSize: theme.typography.customFontSize[6],
                    lineHeight: 1.1,
                    fontWeight: theme.typography.customFontWeight[3],
                    color: statusColorMap[analysisData.status],
                  }}
                >
                  {analysisData.status}
                </Typography>
              </Box>
              <Box>
                <Typography
                  sx={{
                    fontSize: theme.typography.customFontSize[1],
                    fontWeight: theme.typography.customFontWeight[3],
                    color: theme.palette.primary.greyPrimary,
                    mb: 0.5,
                  }}
                >
                  Total APs
                </Typography>

                <Typography
                  sx={{
                    fontSize: theme.typography.customFontSize[6],
                    fontWeight: theme.typography.customFontWeight[3],
                    lineHeight: 1,
                    color: theme.palette.primary.main,
                  }}
                >
                  {analysisData.totalAps}
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                borderTop: `1px solid ${theme.palette.primary.greyThread}`,
                pt: 2.5,
              }}
            >
              <Typography
                sx={{
                  fontSize: theme.typography.customFontSize[1],
                  color: theme.palette.primary.greyPrimary,
                  mb: 1,
                  fontWeight: theme.typography.customFontWeight[3],
                }}
              >
                Peak Congested:{" "}
                <Box
                  component="span"
                  sx={{
                    color: theme.palette.primary.mainHover,
                    fontWeight: theme.typography.customFontWeight[3],
                  }}
                >
                  {analysisData.peakCongested}
                </Box>
              </Typography>

              <Typography
                sx={{
                  fontSize: theme.typography.customFontSize[1],
                  color: theme.palette.primary.greyPrimary,
                  mb: 1,
                  fontWeight: theme.typography.customFontWeight[3],
                }}
              >
                Average Congested:{" "}
                <Box
                  component="span"
                  sx={{
                    color: theme.palette.primary.mainHover,
                    fontWeight: theme.typography.customFontWeight[3],
                  }}
                >
                  {analysisData.averageCongested}
                </Box>
              </Typography>

              <Typography
                sx={{
                  fontSize: theme.typography.customFontSize[1],
                  color: theme.palette.primary.greyPrimary,
                  fontWeight: theme.typography.customFontWeight[3],
                }}
              >
                Client Capacity Threshold:{" "}
                <Box
                  component="span"
                  sx={{
                    color: theme.palette.primary.mainHover,
                    fontWeight: theme.typography.customFontWeight[3],
                  }}
                >
                  {analysisData.threshold}
                </Box>
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              flex: 1,
              pl: { xs: 0, lg: 5 },
              pt: { xs: 1, lg: 0 },
            }}
          >
            <WirelessCongestionLevelChart
              data={wirelessCongestionChartSeriesData}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default WirelessCongestionLevel;
